// フィルタの選択肢を 1 つの表で定義する。分布は既知なので、ユーザーが妥当な数値を
// 知らなくても選べるよう自由入力ではなく選択式にする。表を分岐に散らさないことで、
// 選択肢を追加・変更する変更点を 1 箇所に閉じる。
// 表は「どの値を出すか」だけを持ち、表示文字列は choiceLabel / choiceShort が値から組み立てる。
// そのため URL 由来の選択肢外の値も同じ規則で書ける（値を黙って落とさない）。
// label は読み上げ・title 用の完全な文、short は pill に収まる長さの表示用。
import { DEFAULT_LANG, formatNumber, type Lang } from "./i18n";

/** 選択肢に添える補足。「その値がどちら向きか」を初見で分かるようにする。 */
export type ChoiceNote = "low" | "cool";

export interface Choice {
  value: number;
  note?: ChoiceNote;
}

export const FILTER_CHOICES = {
  floodMax: [{ value: 0.01 }, { value: 0.05 }, { value: 0.1 }, { value: 0.25 }, { value: 0.5 }],
  tsunamiMax: [{ value: 0.01 }, { value: 0.05 }, { value: 0.1 }, { value: 0.2 }],
  sedimentMax: [{ value: 0.01 }, { value: 0.05 }, { value: 0.1 }, { value: 0.2 }],
  // J-SHIS の公式区分 0.1/3/6/26% に合わせる
  quakeMax: [{ value: 0.03, note: "low" }, { value: 0.06 }, { value: 0.26 }, { value: 0.5 }],
  landPriceMax: [
    { value: 10000 },
    { value: 20000 },
    { value: 30000 },
    { value: 50000 },
    { value: 100000 },
    { value: 200000 },
  ],
  populationMin: [
    { value: 5000 },
    { value: 10000 },
    { value: 30000 },
    { value: 50000 },
    { value: 100000 },
    { value: 300000 },
  ],
  tempJanMin: [{ value: -5 }, { value: 0 }, { value: 3 }, { value: 5 }, { value: 8 }],
  tempAugMax: [{ value: 22, note: "cool" }, { value: 24 }, { value: 26 }, { value: 27 }],
  hubDistMax: [
    { value: 50 },
    { value: 100 },
    { value: 150 },
    { value: 200 },
    { value: 300 },
    { value: 500 },
  ],
} as const satisfies Record<string, readonly Choice[]>;

export type FilterChoiceField = keyof typeof FILTER_CHOICES;

const NOTE_TEXT: Record<Lang, Record<ChoiceNote, string>> = {
  ja: { low: "（低い）", cool: "（涼しい）" },
  en: { low: " (low)", cool: " (cool)" },
};

/** 津波の「指定が無い市区町村のみ」（= tsunamiInlandOnly）を表す value。 */
export const TSUNAMI_INLAND_VALUE = "inland";

/** value 文字列 → number|null。空文字列は「指定なし」。 */
export function parseSelectNumber(value: string): number | null {
  if (value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** number|null → value 文字列。 */
export function formatSelectNumber(value: number | null): string {
  return value === null ? "" : String(value);
}

function ratioPercent(value: number): string {
  return `${Math.round(value * 1000) / 10}%`;
}

function manYen(value: number): boolean {
  return value >= 10000 && value % 10000 === 0;
}

/**
 * 読み上げ・title 用の完全なラベル。フィールドごとの単位と向き（上限 / 下限）を
 * 値から組み立てるので、選択肢の表に無い値でも同じ言い回しになる。
 */
export function choiceLabel(
  field: FilterChoiceField,
  value: number,
  lang: Lang = DEFAULT_LANG,
  note?: ChoiceNote,
): string {
  return `${bareLabel(field, value, lang)}${note ? NOTE_TEXT[lang][note] : ""}`;
}

function bareLabel(field: FilterChoiceField, value: number, lang: Lang): string {
  const ja = lang === "ja";
  switch (field) {
    case "floodMax":
    case "tsunamiMax":
    case "sedimentMax":
    case "quakeMax":
      return ja ? `${ratioPercent(value)} 以下` : `≤ ${ratioPercent(value)}`;
    case "landPriceMax":
      if (ja) {
        return manYen(value)
          ? `${value / 10000} 万円/m2 以下`
          : `${formatNumber(value, lang)} 円/m2 以下`;
      }
      return `≤ ${formatNumber(value, lang)} JPY/m²`;
    case "populationMin":
      return ja ? `${formatNumber(value, lang)} 人 以上` : `≥ ${formatNumber(value, lang)} people`;
    case "tempJanMin":
      return ja ? `${value}℃ 以上` : `≥ ${value}℃`;
    case "tempAugMax":
      return ja ? `${value}℃ 以下` : `≤ ${value}℃`;
    case "hubDistMax":
      return ja ? `${value} km 以内` : `≤ ${value} km`;
  }
}

/** pill に収まる長さの表示用ラベル。 */
export function choiceShort(field: FilterChoiceField, value: number, lang: Lang = DEFAULT_LANG): string {
  switch (field) {
    case "floodMax":
    case "tsunamiMax":
    case "sedimentMax":
    case "quakeMax":
      return ratioPercent(value);
    case "landPriceMax":
      if (lang === "ja") {
        return manYen(value) ? `${value / 10000}万` : `${formatNumber(value, lang)}円`;
      }
      return manYen(value) ? `${value / 1000}k` : formatNumber(value, lang);
    case "populationMin":
      if (lang === "ja") {
        return manYen(value) ? `${value / 10000}万` : `${formatNumber(value, lang)}人`;
      }
      return manYen(value) ? `${value / 1000}k` : formatNumber(value, lang);
    case "tempJanMin":
    case "tempAugMax":
      return `${value}℃`;
    case "hubDistMax":
      return `${value}km`;
  }
}

/** 津波の value → { tsunamiMax, tsunamiInlandOnly }。1 つの選択で 2 フィールドを表す変換をここに閉じる。 */
export function tsunamiSelectToState(value: string): { tsunamiMax: number | null; tsunamiInlandOnly: boolean } {
  if (value === TSUNAMI_INLAND_VALUE) return { tsunamiMax: null, tsunamiInlandOnly: true };
  return { tsunamiMax: parseSelectNumber(value), tsunamiInlandOnly: false };
}

/** { tsunamiMax, tsunamiInlandOnly } → 津波の value。 */
export function tsunamiStateToSelectValue(tsunamiMax: number | null, tsunamiInlandOnly: boolean): string {
  if (tsunamiInlandOnly) return TSUNAMI_INLAND_VALUE;
  return formatSelectNumber(tsunamiMax);
}
